import { MappedClass, MappedProperty, MappedPropertyArray, MappedPropertyOptional } from '@h5s/util-object';
import { Department, VatRate } from '../../settings/entities';
import { CashRegister } from './cash-register';
import { CashRegisterConfigurablePayment } from './cash-register-configurable-payment';
import { ZPrinterFiscalConfigDepartmentIn } from './z-printer-fiscal-config-department-in';
import { ZPrinterFiscalConfigPaymentIn } from './z-printer-fiscal-config-payment-in';
import { ZPrinterFiscalConfigVatIn } from './z-printer-fiscal-config-vat-in';
import { ZPrinterManufacturer } from './z-printer-paper-manufacturer';

// I campi che questa configurazione non gestisce sono dichiarati @MappedPropertyOptional: restano undefined e vengono omessi dal payload.
// Vanno marcati uno per uno invece di passare ignoreUndefined globale amapParamDto, altrimenti si spegne anche il controllo dev-mode sui campi obbligatori (register_number, gli index delle aliquote/reparti...), che verrebbero inviati undefined a una stampante fiscale senza avvisi.
@MappedClass()
export class ZPrinterFiscalConfigRequest {
  @MappedPropertyOptional()
  print_exempted_products: boolean | undefined;

  @MappedPropertyOptional()
  first_courtesy_line: string | undefined;

  @MappedPropertyOptional()
  second_courtesy_line: string | undefined;

  // Inviarlo come array vuoto rischierebbe di azzerare le righe di intestazione della stampante
  @MappedPropertyOptional()
  headings: string[] | undefined;

  @MappedPropertyOptional()
  main_logo: string | undefined;

  @MappedPropertyOptional()
  secondary_logo: string | undefined;

  @MappedPropertyOptional()
  department_net: boolean | undefined;

  @MappedPropertyOptional()
  midnight_alert: boolean | undefined;

  @MappedPropertyOptional()
  print_operator: boolean | undefined;

  @MappedPropertyOptional()
  print_device_number: boolean | undefined;

  @MappedPropertyOptional()
  single_quantity: boolean | undefined;

  @MappedPropertyOptional()
  print_unit_price: boolean | undefined;

  @MappedPropertyOptional()
  show_change: boolean | undefined;

  @MappedPropertyOptional()
  show_single_quantity: boolean | undefined;

  @MappedPropertyOptional()
  sliding_message: string | undefined;

  @MappedPropertyOptional()
  show_sliding_message: boolean | undefined;

  @MappedPropertyOptional()
  sliding_message_timeout: number | undefined;

  @MappedPropertyOptional()
  drawer_pulse: number | undefined;

  @MappedPropertyOptional()
  receipt_limit: number | undefined;

  @MappedPropertyOptional()
  appendix_cut: boolean | undefined;

  @MappedPropertyOptional()
  vat_ventilation: boolean | undefined;

  @MappedPropertyOptional()
  print_buffer: boolean | undefined;

  @MappedPropertyOptional()
  is_fidelity_enabled: boolean | undefined;

  @MappedPropertyOptional()
  is_cutter_enabled: boolean | undefined;

  @MappedPropertyArray(() => ZPrinterFiscalConfigVatIn)
  vats: ZPrinterFiscalConfigVatIn[] = [];

  @MappedPropertyArray(() => ZPrinterFiscalConfigDepartmentIn)
  departments: ZPrinterFiscalConfigDepartmentIn[] = [];

  @MappedPropertyArray(() => ZPrinterFiscalConfigPaymentIn)
  payments: ZPrinterFiscalConfigPaymentIn[] = [];

  @MappedPropertyOptional()
  auto_open_drawer: boolean | undefined;

  @MappedPropertyOptional()
  print_header_on_reports: boolean | undefined;

  @MappedPropertyOptional()
  print_x_before_z_report: boolean | undefined;

  @MappedPropertyOptional()
  print_transmission_report: boolean | undefined;

  @MappedPropertyOptional()
  header_logo_index: number | undefined;

  @MappedPropertyOptional()
  footer_logo_index: number | undefined;

  @MappedProperty()
  register_number: number;

  @MappedPropertyOptional()
  logo_alignment: number | undefined;

  @MappedPropertyOptional()
  rt_transmission_retry_minutes: number | undefined;

  @MappedPropertyOptional()
  rounding_mode: number | undefined;

  @MappedPropertyOptional()
  communication_pc: number | undefined;

  @MappedPropertyOptional()
  lottery: boolean | undefined;

  @MappedPropertyOptional()
  ssn_service: boolean | undefined;

  public fromSources(
    cashRegister: CashRegister,
    departments: Department[],
    vatRates: VatRate[],
    payments: CashRegisterConfigurablePayment[],
    manufacturer: ZPrinterManufacturer,
  ): void {
    this.register_number = cashRegister.id;

    this.vats = vatRates.map((vr) => {
      const vat = new ZPrinterFiscalConfigVatIn();
      vat.fromVatRate(vr, manufacturer);
      return vat;
    });

    // Riusiamo l'indice già calcolato per ogni aliquota (percentuale libera o natura a casella fissa),
    // invece di ricalcolarlo, per evitare due logiche di risoluzione dell'indice che potrebbero divergere
    const vatIndexById = new Map<number, number>(vatRates.map((vr, i) => [vr.id, this.vats[i].index]));

    this.departments = departments.map((dep) => {
      const department = new ZPrinterFiscalConfigDepartmentIn();
      department.fromDepartment(dep, vatIndexById);
      return department;
    });

    this.payments = payments.map((p) => {
      const payment = new ZPrinterFiscalConfigPaymentIn();
      payment.fromPayment(p);
      return payment;
    });
  }
}
